import type { HttpMethod, QueryParamsType } from '@blufin/types';
import { Base64 } from '../Base64/Base64';
import { Sha256 } from '../Sha256/Sha256';
import { HmacUtils } from './helpers/HmacUtils';

/**
 * If the server's current time is t, then timestamps within the range [t - ALLOWED_DELTA, t + ALLOWED_DELTA] would be accepted.
 * This logic is intended to account for latency between the client and server.
 */
export const ALLOWED_DELTA = 15000; // 15 seconds.

export const HMAC_CONTENT_LENGTH_MISMATCH_ERROR = 'HMAC content length mismatch.';
export const HMAC_TIMESTAMP_OUTSIDE_DELTA_ERROR = 'HMAC request timestamp is outside the allowed time delta.';
export const HMAC_SIGNATURE_MISMATCH_ERROR = 'HMAC signature mismatch.';

export type HmacComputeData = {
  body?: any;
  method: HttpMethod;
  queryParams?: QueryParamsType;
  url?: string;
};

export type HmacVerifyData = {
  body?: any;
  contentLength: number;
  method: HttpMethod;
  queryParams?: QueryParamsType;
  url?: string;
};

export interface IHmac {
  compute(
    secret: string,
    requestData: HmacComputeData,
    timestampOffset?: number
  ): { contentLength: number; hash: string; timestamp: number };
  deriveToken(label: string): string;
  verify(secret: string, requestData: HmacVerifyData, timestamp: number, expectedHash: string): void;
}

export const Hmac: IHmac = {
  /**
   * Computes the hash signature for the given request data.
   * For timestampOffset - Positive value: Server time is ahead of client time. Negative value: Client time is ahead of server time.
   * @param secret - The secret key used to compute the hash.
   * @param requestData - The request data to be used to compute the hash.
   * @param timestampOffset - Optional offset in milliseconds to adjust for clock skew between client and server.
   */
  compute: (
    secret: string,
    requestData: HmacComputeData,
    timestampOffset?: number
  ): { contentLength: number; hash: string; timestamp: number } => {
    const timestamp = Date.now() + (timestampOffset || 0);
    const contentLength = HmacUtils.getContentLength(requestData.body);
    const standardizedRequest = HmacUtils.standardizeRequest({ ...requestData, contentLength }, timestamp);
    const hash = Sha256.hmac(secret, standardizedRequest);
    return { contentLength, hash, timestamp };
  },
  /**
   * Deterministically derives an opaque 32-char runtime token from a human-readable, non-secret label.
   * Used at CDK synth time to turn the friendly `hmacToken` config label (eg: `hmac-authservicestaff-prod-x7k2m9`)
   * into the actual `BF_HMAC_SECRET` value handed to browsers via `GET /v1/hmac`. The HMAC token is not a real
   * secret — it is a public spam / rate-limiting deterrent — so this derivation only needs to be stable and opaque.
   * Uses only browser-safe `@blufin/security` primitives (no Node `crypto`): sha256 → Base64 → strip non-alphanumerics → slice(32).
   * IMPORTANT: This algorithm is FROZEN after the first deploy. Changing it re-derives every token and invalidates
   * every cached client token (they 401 once, clear localStorage, and refetch) — acceptable for a one-time cutover, not as churn.
   * @param label - The public, non-secret config label to derive from.
   * @returns A deterministic 32-char mixed-case alphanumeric token.
   */
  deriveToken: (label: string): string => {
    return Base64.encode(Sha256.hash(label))
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 32);
  },
  /**
   * Verifies the hash signature for the given request data.
   * @param secret - The secret key used to compute the hash.
   * @param requestData - The request data to be used to compute the hash.
   * @param timestamp - The timestamp when the hash was computed on the client-side.
   * @param expectedHash - The expected hash signature.
   */
  verify: (secret: string, requestData: HmacVerifyData, timestamp: number, expectedHash: string): void => {
    const elapsedTimeInMillis = HmacUtils.getTimeElapsedInMillis(timestamp);
    /** Check elapsed time is within the allowed delta. */
    if (Math.abs(elapsedTimeInMillis) > ALLOWED_DELTA) throw new Error(HMAC_TIMESTAMP_OUTSIDE_DELTA_ERROR);
    const contentLength = HmacUtils.getContentLength(requestData.body);
    if (contentLength !== requestData.contentLength) throw new Error(HMAC_CONTENT_LENGTH_MISMATCH_ERROR);
    const standardizedRequest = HmacUtils.standardizeRequest(requestData, timestamp);
    /** Check the hash signature using constant-time comparison to prevent timing attacks. */
    const computedHash = Sha256.hmac(secret, standardizedRequest);
    if (!_constantTimeCompare(computedHash, expectedHash)) throw new Error(HMAC_SIGNATURE_MISMATCH_ERROR);
  }
};

/**
 * Compares two hex strings in constant time to prevent timing attacks.
 * Uses XOR accumulation to ensure all characters are compared regardless of mismatch position.
 * This is a browser-compatible implementation that doesn't rely on Node.js crypto.
 * @param a - First hex string.
 * @param b - Second hex string.
 * @returns True if the strings are equal, false otherwise.
 */
const _constantTimeCompare = (a: string, b: string): boolean => {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
};
